/**
 * Cover-stats payload normalize — wall total vs art corpus.
 *
 * Connector `local_library_get_cover_stats` returns both:
 *   total            = wall (every indexed album)
 *   artCorpusTotal   = albums with an art source (artwork_id or cover_art_url)
 * Held boot snapshots historically used snake_case (`with_cover`) and omitted
 * corpus fields. Dropping corpus made the banner denominator flip 626 ↔ 628
 * and treat a held snapshot as "all albums pending".
 */

export type NormalizedCoverStats = {
  total: number;
  withCover: number;
  withoutCover: number;
  embedded: number;
  folder: number;
  online: number;
  none: number;
  overridden: number;
  artCorpusTotal: number;
  artTerminal: number;
  placeholderTerminal: number;
  heldDuringScan: boolean;
  version?: string | number | null;
};

function num(...candidates: unknown[]): number {
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
    if (n === 0) return 0;
  }
  return 0;
}

export function normalizeCoverStats(payload: Record<string, unknown> | null | undefined): NormalizedCoverStats {
  const total = num(payload?.total);
  const withCover = num(payload?.withCover, payload?.with_cover);
  const withoutCover = num(payload?.withoutCover, payload?.without_cover, payload?.none);
  return {
    total,
    withCover,
    withoutCover,
    embedded: num(payload?.embedded, payload?.withCover, payload?.with_cover),
    folder: num(payload?.folder),
    online: num(payload?.online, payload?.enrichment),
    none: withoutCover,
    overridden: num(payload?.overridden),
    artCorpusTotal: num(payload?.artCorpusTotal, payload?.art_corpus_total),
    artTerminal: num(payload?.artTerminal, payload?.art_terminal),
    placeholderTerminal: num(payload?.placeholderTerminal, payload?.placeholder_terminal),
    heldDuringScan: payload?.held_during_scan === true || payload?.heldDuringScan === true,
    version: (payload?.version as string | number | null | undefined) ?? null,
  };
}

export type ArtworkCorpusSnap = {
  /** Banner denominator — art corpus when known, never wall-vs-corpus mix. */
  total: number;
  pending: number;
  complete: boolean;
  withCover: number;
  artTerminal: number;
  placeholderTerminal: number;
};

/**
 * Stable "Finding covers & details" denominator.
 * Wall `total` includes no-art-source albums (628); corpus excludes them (626).
 */
export function artworkCorpusFromCoverStats(
  stats: NormalizedCoverStats | Record<string, unknown> | null | undefined,
): ArtworkCorpusSnap {
  const n = normalizeCoverStats(stats as Record<string, unknown> | null | undefined);

  const artCorpusTotal = n.artCorpusTotal;
  const artTerminal = n.artTerminal;
  const wallTotal = n.total;
  const total = artCorpusTotal > 0 ? artCorpusTotal : wallTotal;
  const complete = artCorpusTotal > 0 && artTerminal >= artCorpusTotal;
  const pending = complete
    ? 0
    : (n.withoutCover > 0 ? n.withoutCover : Math.max(0, total - n.withCover));
  return {
    total,
    pending,
    complete,
    withCover: n.withCover,
    artTerminal,
    placeholderTerminal: n.placeholderTerminal,
  };
}
